import { inject, Injectable } from '@angular/core';
import { Option } from '@sapphire/result';

import { AuthSelectorsFacade } from '@ps/auth-data-access';
import { UserSettingsSelectorsFacade } from '@ps/user-settings-data-access';

import { SurveyAudienceForm, SurveyLaunchSubmissionForm } from '../../utils';
import { actingUser } from '../../state/core/utils/survey-save-pipeline.helper';
import {
  ProjectSaveMessages,
  ProjectUpdateInput,
} from '../../state/audience/utils/project-save.helper';
import { SurveyCreationSelectorsFacade } from '../../state/survey-creation.selectors.facade';
import { SurveyChildEntry } from '../../state/survey-creation-child-registry.helper';
import { DEFAULT_CORE_CHILD_STATE } from '../../state/survey-creation-state.constants';

/**
 * Everything `buildProjectUpdate` needs, read from the store in one place so a
 * save and a membership change describe the same project rather than each
 * gathering its own view of it.
 */
@Injectable({ providedIn: 'root' })
export class ProjectUpdateInputBuilder {
  private readonly _selectors = inject(SurveyCreationSelectorsFacade);
  private readonly _authSelectors = inject(AuthSelectorsFacade);
  private readonly _userSettingsSelectors = inject(UserSettingsSelectorsFacade);

  public build(messages: ProjectSaveMessages): Option<ProjectUpdateInput> {
    const coreChildren = this._selectors.coreChildren();
    const audienceChildren = this._selectors.audienceChildren();
    const user = actingUser(this._authSelectors);

    return Option.from(this._selectors.rootSurveyId())
      .andThen((projectId) => Option.from(coreChildren[projectId]?.survey))
      .andThen((project) =>
        Option.from(
          SurveyAudienceForm.from(this._selectors.surveyFormState()).data,
        ).map((activeForm) => ({ project, activeForm })),
      )
      .andThen(({ project, activeForm }) =>
        Option.from(
          SurveyLaunchSubmissionForm.from(this._selectors.launchForm()).data,
        ).map(
          (launch): ProjectUpdateInput => ({
            project,
            activeMetadata: activeForm.metadata,
            children: Object.entries(audienceChildren).map(
              ([key, child]): SurveyChildEntry => [
                key,
                child,
                coreChildren[key] ?? DEFAULT_CORE_CHILD_STATE,
              ],
            ),
            launch,
            pricing: {
              ...this._selectors.pricing(),
              feasibilityCpi: this._selectors.feasibilityCpi(),
            },
            insightsChildren: this._selectors.insightsChildren(),
            qualificationsRecord:
              this._selectors.participantQualificationsRecord(),
            userId: user.id,
            companyId: user.companyId,
            enableRespondentStatusFilter:
              !!this._userSettingsSelectors.buyerSettings()
                .enableRespondentStatusFilter,
            messages,
          }),
        ),
      );
  }
}
